'use client';

import * as React from 'react';
import Link from 'next/link';
import { addDays, format } from 'date-fns';
import {
  ArrowRightIcon,
  CheckIcon,
  ChevronRightIcon,
  SparklesIcon
} from 'lucide-react';

import { routes } from '@workspace/routes';
import { Badge } from '@workspace/ui/components/badge';
import { Button, buttonVariants } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';

import { formatCurrency, formatPrice, getPrimaryPrice } from '../helpers';
import { TRIAL_INCLUDES_SUMMARY, TRIAL_UNLOCK_NOTE } from '../trial';
import {
  Plan,
  Price,
  PriceInterval,
  PriceModel,
  PriceType,
  Product,
  Tier
} from '../schema';

export type PricingTestimonial = {
  quote: string;
  name: string;
};

export type PricingCardProps = React.HtmlHTMLAttributes<HTMLDivElement> & {
  pending?: boolean;
  product: Product;
  plan: Plan;
  selectedInterval: PriceInterval;
  isCurrent?: boolean;
  quantity?: number;
  /**
   * When false, suppresses the trial CTA/copy for orgs that are not eligible
   * for a trial (already had a paid subscription). Defaults to true so the
   * marketing app (which has no org context) is unaffected.
   */
  trialEligible?: boolean;
  /** Shown on the recommended card only (replaces the mock's ratings box). */
  featuredTestimonial?: PricingTestimonial;
  onUpgrade?: (productId: string, planId: string, quantity?: number) => void;
  /** Analytics-only hook fired when the plan CTA is clicked (both link and button variants). */
  onPlanCtaClick?: (
    productId: string,
    planId: string,
    interval: PriceInterval
  ) => void;
};

export function PricingCard({
  pending,
  product,
  plan,
  selectedInterval,
  isCurrent = false,
  quantity,
  trialEligible = true,
  featuredTestimonial,
  onUpgrade,
  onPlanCtaClick,
  className,
  ...other
}: PricingCardProps): React.JSX.Element {
  const primaryPrice = getPrimaryPrice(plan);
  const meteredPrice = plan.prices.find(
    (price) => price.model === PriceModel.Metered || !!price.meter
  );

  // Yearly savings vs. paying monthly for a year (original list price when a
  // launch discount is shown), derived from the sibling month plan (percent
  // for the interval pill, dollars for the "billed" note).
  const monthPlan = product.plans.find((p) =>
    p.displayIntervals.includes(PriceInterval.Month)
  );
  const monthPrice = monthPlan ? getPrimaryPrice(monthPlan) : undefined;
  const isYear =
    selectedInterval === PriceInterval.Year &&
    primaryPrice?.type === PriceType.Recurring;
  const yearlyBaseline =
    isYear && monthPrice && monthPrice.cost > 0
      ? (monthPrice.originalCost ?? monthPrice.cost) * 12
      : undefined;
  const yearlySavingsPercent =
    isYear && primaryPrice && primaryPrice.cost > 0 && yearlyBaseline
      ? Math.round((1 - primaryPrice.cost / yearlyBaseline) * 100)
      : undefined;
  const yearlySavingsAmount =
    isYear && primaryPrice && yearlyBaseline
      ? yearlyBaseline - primaryPrice.cost
      : undefined;

  const effectiveQuantity = quantity && quantity > 1 ? quantity : undefined;
  const isCreditPack = product.id.startsWith('credit-pack-');
  const effectiveTrialDays = trialEligible ? plan.trialDays : undefined;
  // Not gated on onUpgrade: marketing surfaces (no onUpgrade handler) must
  // show the same "$0 today…" fine print and trial-inclusion line as the
  // dashboard — the public pricing page had no trial qualifier at all.
  const isTrialCta = Boolean(effectiveTrialDays) && !product.isEnterprise;
  const isRecommended = Boolean(product.recommended);
  const featureGroups = product.featureGroups ?? [];

  return (
    <div
      className={cn(
        'relative flex h-full w-full flex-col rounded-2xl bg-card px-6 py-6 shadow-sm transition-[transform,box-shadow] duration-200',
        isRecommended
          ? 'border-2 border-primary shadow-lg shadow-primary/10 hover:shadow-xl hover:shadow-primary/15 md:-translate-y-1.5'
          : 'border border-border hover:shadow-md',
        className
      )}
      {...other}
    >
      {isRecommended && (
        <div className="absolute -top-3 left-0 flex w-full justify-center">
          <Badge className="rounded-full px-3 py-1 shadow-sm">
            Recommended
          </Badge>
        </div>
      )}
      <div className="flex flex-1 flex-col gap-y-5">
        <ProductDetails
          name={product.name}
          headline={product.headline}
          description={product.description}
        />
        {primaryPrice && (
          <PriceInfo
            isEnterprise={product.isEnterprise}
            primaryPrice={primaryPrice}
            selectedInterval={selectedInterval}
            launchOffer={product.launchOffer}
            quantity={effectiveQuantity}
            savingsPercent={yearlySavingsPercent}
            savingsAmount={yearlySavingsAmount}
          />
        )}
        <CheckoutButton
          product={product}
          plan={plan}
          isCurrent={isCurrent}
          pending={pending}
          quantity={quantity}
          trialDays={effectiveTrialDays}
          onUpgrade={onUpgrade}
          onCtaClick={
            onPlanCtaClick
              ? () => onPlanCtaClick(product.id, plan.id, selectedInterval)
              : undefined
          }
        />
        {isTrialCta && effectiveTrialDays && primaryPrice && (
          <p className="text-center text-xs leading-relaxed text-muted-foreground">
            $0 today — your plan starts{' '}
            {format(addDays(new Date(), effectiveTrialDays), 'MMM d, yyyy')} at{' '}
            {formatCurrency(primaryPrice.cost, primaryPrice.currency)}
            {isYear ? '/yr' : '/mo'} unless you cancel. Cancel anytime. Card
            required to start.
          </p>
        )}
        {isTrialCta && (
          <p className="text-center text-xs leading-relaxed text-muted-foreground">
            Free trial includes {TRIAL_INCLUDES_SUMMARY}. {TRIAL_UNLOCK_NOTE}
          </p>
        )}
        {isRecommended && featuredTestimonial && (
          <FeaturedTestimonial testimonial={featuredTestimonial} />
        )}
        <div className="border-t border-dashed border-border" />
        {featureGroups.length > 0 ? (
          <div className="flex flex-col gap-y-5">
            {featureGroups.map((group) => (
              <div key={group.label}>
                <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">
                  {group.label}
                </p>
                <ul className="space-y-2.5">
                  {group.features.map((feature) => (
                    <FeatureDisplayItem
                      key={feature}
                      feature={feature}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <ul className="space-y-2.5">
            {product.features.map((feature) => (
              <FeatureDisplayItem
                key={feature}
                feature={
                  isCreditPack && effectiveQuantity
                    ? scaleFeature(feature, effectiveQuantity)
                    : feature
                }
              />
            ))}
            {meteredPrice?.meter?.tiers.map((tier, index) => (
              <MeteredTierItem
                key={index}
                tier={tier}
                index={index}
                allTiers={meteredPrice.meter!.tiers}
                currency={meteredPrice.currency}
                unit={meteredPrice.meter!.unit || ''}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * Scale numeric values in a feature string by a given quantity.
 * e.g. "30 mins of video upload" with quantity 2 becomes "60 mins of video upload"
 */
function scaleFeature(feature: string, quantity: number): string {
  return feature.replace(/^(\d+)\s/, (_, num) => {
    return `${parseInt(num, 10) * quantity} `;
  });
}

/**
 * Check icon in a soft muted circle. Shared by the pricing feature lists.
 */
export function CheckBullet({
  className
}: {
  className?: string;
}): React.JSX.Element {
  return (
    <span
      className={cn(
        'mt-0.5 flex size-[18px] shrink-0 items-center justify-center rounded-full bg-muted',
        className
      )}
    >
      <CheckIcon
        strokeWidth={3}
        className="size-3 text-muted-foreground"
      />
    </span>
  );
}

type ProductDetailsProps = {
  name: string;
  headline?: string;
  description: string;
};

function ProductDetails({ name, headline, description }: ProductDetailsProps) {
  return (
    <div className="flex flex-col gap-y-2">
      <span className="text-xs font-bold uppercase tracking-[0.08em] text-secondary-foreground">
        {name}
      </span>
      {/* Reserve worst-case line counts so sibling cards keep their price rows
          and CTAs aligned across the row. */}
      {headline && (
        <p className="font-heading text-[19px] font-bold leading-snug tracking-tight text-foreground sm:min-h-[3lh]">
          {headline}
        </p>
      )}
      <p
        className={cn(
          'text-sm leading-normal text-muted-foreground',
          headline && 'sm:min-h-[4lh]'
        )}
      >
        {description}
      </p>
    </div>
  );
}

type PriceInfoProps = {
  isEnterprise?: boolean;
  primaryPrice: Price;
  selectedInterval: PriceInterval;
  launchOffer?: string;
  quantity?: number;
  savingsPercent?: number;
  savingsAmount?: number;
};

function PriceInfo({
  isEnterprise,
  primaryPrice,
  selectedInterval,
  launchOffer,
  quantity,
  savingsPercent,
  savingsAmount
}: PriceInfoProps) {
  const showTotalPrice =
    quantity && quantity > 1 && primaryPrice.type === PriceType.OneTime;
  const totalCost = showTotalPrice ? primaryPrice.cost * quantity : undefined;
  const isYear =
    selectedInterval === PriceInterval.Year &&
    primaryPrice.type === PriceType.Recurring;
  const currency = primaryPrice.currency.toLowerCase();

  return (
    <div className="flex flex-col">
      <div className="flex items-end gap-x-2">
        {primaryPrice.originalCost && (
          <span className="pb-1 text-xl leading-none text-muted-foreground line-through tabular-nums">
            {formatCurrency(primaryPrice.originalCost, currency)}
          </span>
        )}
        <span className="font-heading text-4xl/[42px] font-extrabold tracking-tight text-foreground tabular-nums">
          {isEnterprise
            ? 'Custom'
            : showTotalPrice && totalCost !== undefined
              ? formatCurrency(totalCost, currency)
              : formatPrice(primaryPrice, selectedInterval)}
        </span>
        {!isEnterprise && (
          <>
            {primaryPrice.type === PriceType.Recurring && (
              <span className="pb-1 text-sm leading-none text-muted-foreground">
                /mo
              </span>
            )}
            {primaryPrice.model === PriceModel.PerSeat && (
              <span className="pb-1 text-sm leading-none text-muted-foreground">
                /seat
              </span>
            )}
          </>
        )}
      </div>
      {!isEnterprise && isYear && (
        <p className="mt-1.5 text-xs text-muted-foreground tabular-nums">
          Billed {formatCurrency(primaryPrice.cost, currency)}/yr
          {savingsAmount && savingsAmount > 0 ? (
            <span className="font-medium text-primary">
              {' · save '}
              {formatCurrency(savingsAmount, currency)}/yr
              {savingsPercent ? ` (${savingsPercent}%)` : ''}
            </span>
          ) : null}
        </p>
      )}
      {showTotalPrice && (
        <p className="mt-1 text-xs text-muted-foreground tabular-nums">
          {formatCurrency(primaryPrice.cost, currency)} each x {quantity}
        </p>
      )}
      {launchOffer && primaryPrice.originalCost && (
        <div className="mt-2 flex items-center gap-1.5 text-sm font-medium text-primary">
          <SparklesIcon className="size-4 shrink-0" />
          <span>{launchOffer}</span>
        </div>
      )}
    </div>
  );
}

type CheckoutButtonProps = {
  product: Product;
  plan: Plan;
  isCurrent: boolean;
  pending?: boolean;
  quantity?: number;
  trialDays?: number | null;
  onUpgrade?: (productId: string, planId: string, quantity?: number) => void;
  onCtaClick?: () => void;
};

function CheckoutButton({
  product,
  plan,
  isCurrent,
  pending,
  quantity,
  trialDays,
  onUpgrade,
  onCtaClick
}: CheckoutButtonProps) {
  const commonClasses =
    'group h-12 w-full rounded-xl text-[15px] font-bold shadow-none transition-all duration-200';
  const isFilled = product.recommended || Boolean(trialDays);
  const isTrial = Boolean(trialDays) && !product.isEnterprise;
  const CtaIcon = isTrial ? ArrowRightIcon : ChevronRightIcon;
  const ctaIcon = !isCurrent && (
    <CtaIcon className="ml-1 size-4 transition-transform group-hover:translate-x-0.5" />
  );

  if (onUpgrade) {
    const buttonLabel = isCurrent
      ? 'Current Plan'
      : trialDays
        ? `Start ${trialDays}-Day Free Trial`
        : product.label;

    return (
      <Button
        variant={isFilled ? 'default' : 'outline'}
        className={commonClasses}
        disabled={isCurrent || pending}
        onClick={() => {
          onCtaClick?.();
          onUpgrade(product.id, plan.id, quantity);
        }}
      >
        {buttonLabel}
        {ctaIcon}
      </Button>
    );
  }

  // Marketing context (no upgrade handler) — link to sign-up / contact.
  const linkLabel = isCurrent
    ? 'Current Plan'
    : isTrial
      ? 'Start Free Trial'
      : product.label;

  return (
    <Link
      href={
        product.isEnterprise
          ? routes.marketing.Contact
          : routes.dashboard.auth.SignUp
      }
      onClick={onCtaClick}
      className={cn(
        buttonVariants({
          variant: product.recommended ? 'default' : 'outline'
        }),
        commonClasses
      )}
    >
      {linkLabel}
      {ctaIcon}
    </Link>
  );
}

function FeaturedTestimonial({
  testimonial
}: {
  testimonial: PricingTestimonial;
}) {
  return (
    <figure className="rounded-xl bg-muted/50 p-3.5 text-sm">
      <blockquote className="text-foreground/90">
        &ldquo;{testimonial.quote}&rdquo;
      </blockquote>
      <figcaption className="mt-1.5 text-xs font-semibold text-primary">
        — {testimonial.name}
      </figcaption>
    </figure>
  );
}

type FeatureDisplayItemProps = {
  feature: string;
};

function FeatureDisplayItem({ feature }: FeatureDisplayItemProps) {
  return (
    <li className="flex items-start gap-x-2.5">
      <CheckBullet />
      <span className="text-sm leading-snug text-foreground">{feature}</span>
    </li>
  );
}

type MeteredTierItemProps = {
  tier: Tier;
  index: number;
  allTiers: Tier[];
  currency: string;
  unit: string;
};

function MeteredTierItem({
  tier,
  index,
  allTiers,
  currency,
  unit
}: MeteredTierItemProps) {
  const tiersLength = allTiers.length;
  const previousTier = allTiers[index - 1];
  const isLastTier = tier.upTo === Infinity;
  const previousTierFrom =
    previousTier?.upTo === Infinity
      ? 'unlimited'
      : previousTier === undefined
        ? 0
        : (previousTier.upTo || 0) + 1;
  const upTo = tier.upTo;
  const isIncluded = tier.cost === 0;

  return (
    <li className="flex items-start gap-x-2.5">
      <CheckBullet />
      <span className="text-sm leading-snug text-foreground">
        {isLastTier ? (
          <>
            <span>{formatCurrency(tier.cost, currency.toLowerCase())}</span>
            {tiersLength > 1 && (
              <span>
                {' '}
                above {(previousTierFrom as number) - 1} {unit}
              </span>
            )}
            {tiersLength === 1 && <span> for every {unit}</span>}
          </>
        ) : (
          <>
            {isIncluded ? (
              <span>
                {upTo} {unit} / month
              </span>
            ) : (
              <>
                <span>{formatCurrency(tier.cost, currency.toLowerCase())}</span>
                <span>
                  {' '}
                  for each {unit} for the next {upTo} {unit}
                </span>
              </>
            )}
          </>
        )}
      </span>
    </li>
  );
}
